import type { publicApiContract } from '@_/api-contracts/public-api.ts';
import type { ClientOptions } from '@orpc/client';
import type { ContractRouterClient } from '@orpc/contract';
import type { JsonifiedClient } from '@orpc/openapi-client';
import { createORPCClient } from '@orpc/client';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import { createTanstackQueryUtils } from '@orpc/tanstack-query';
import Cookies from 'js-cookie';
import publicApiJson from '@_/api-contracts/public-api.json' with { type: 'json' };
import { SharedConfig } from '@_/config/shared-config.ts';
import { getSignedIpHeaders } from '#src/functions/getSignedIpHeader.ts';

interface PublicApiClientContext {
  locale?: string;
  request?: Request;
}

const publicApiLink = new OpenAPILink<PublicApiClientContext>(
  publicApiJson as typeof publicApiContract,
  {
    url: import.meta.env.ENV_CORE_API_BASE_URL,
    headers: resolveHeaders,
    fetch: (request, init) =>
      globalThis.fetch(request, {
        ...init,
        credentials: 'include'
      })
  }
);

export const publicApiClient: JsonifiedClient<
  ContractRouterClient<typeof publicApiContract, PublicApiClientContext>
> = createORPCClient(publicApiLink);

export const publicApiQueryClient = createTanstackQueryUtils(publicApiClient);

async function resolveHeaders(
  options: ClientOptions<PublicApiClientContext>
): Promise<Record<string, string | undefined>> {
  const locale =
    options.context.locale ||
    Cookies.get(SharedConfig.cookies.locale) ||
    SharedConfig.defaultLocale;

  // Forward user's IP address and cookies if we are on the server
  if (typeof window === 'undefined' && options.context.request) {
    const { request } = options.context;

    const ipHeaders = await getSignedIpHeaders();

    return {
      ...ipHeaders,
      'Accept-Language': locale,
      Cookie: request.headers.get('cookie') || undefined
    };
  }

  return {
    'Accept-Language': locale
  };
}
